/**
 * ProductTechSheetDialog — Editor immagine + campi scheda tecnica di un prodotto.
 *
 * Layout:
 *   [ Immagine (upload/preview/rimuovi) ]  |  [ Form campi IT/EN a 2 colonne ]
 *
 * Campi supportati (tutti opzionali, ognuno con variante _it e _en):
 *   • Peso · Provenienza · Confezione · Pz x confezione · TMC
 *   • Altro 1 (etichetta + valore, personalizzabile per prodotto)
 *   • Altro 2 (etichetta + valore, personalizzabile per prodotto)
 *
 * Al termine: pulsanti "Salva", "Scarica PDF IT", "Scarica PDF EN".
 */
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, FileText, Loader2, X, ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { openAuthenticatedPdf } from "../utils/pdf";
import { API } from "../utils/format";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_TECH = {
  weight_it: "", weight_en: "",
  origin_it: "", origin_en: "",
  packaging_it: "", packaging_en: "",
  pcs_per_pack_it: "", pcs_per_pack_en: "",
  tmc_it: "", tmc_en: "",
  extra_1_label_it: "", extra_1_value_it: "", extra_1_label_en: "", extra_1_value_en: "",
  extra_2_label_it: "", extra_2_value_it: "", extra_2_label_en: "", extra_2_value_en: "",
};

export default function ProductTechSheetDialog({ open, onClose, product, onSaved }) {
  const [tech, setTech] = useState(EMPTY_TECH);
  const [imagePath, setImagePath] = useState(product?.image_path || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open || !product) return;
    setTech({ ...EMPTY_TECH, ...(product.tech_sheet || {}) });
    setImagePath(product.image_path || null);
  }, [open, product]);

  const set = (key, val) => setTech((t) => ({ ...t, [key]: val }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await axios.post(`${API}/products/${product.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImagePath(r.data.path);
      toast.success("Immagine caricata");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload immagine");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleImageDelete = async () => {
    if (!window.confirm("Rimuovere l'immagine dal prodotto?")) return;
    try {
      await axios.delete(`${API}/products/${product.id}/image`);
      setImagePath(null);
      toast.success("Immagine rimossa");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore rimozione");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/products/${product.id}/tech-sheet`, tech);
      toast.success("Scheda tecnica salvata");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = (lang) => {
    openAuthenticatedPdf(`/products/${product.id}/tech-sheet/pdf?lang=${lang}`);
  };

  if (!product) return null;
  const imgSrc = imagePath ? (imagePath.startsWith("http") ? imagePath : `${BACKEND_URL}/api/files/${imagePath}`) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] flex flex-col" data-testid="tech-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl text-stone-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#C5A059]" />
            Scheda tecnica · {product.code}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">{product.description}</DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-2">
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 py-2">
            {/* Immagine */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Immagine prodotto</Label>
              <div className="aspect-square bg-stone-50 border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center overflow-hidden" data-testid="tech-sheet-image-preview">
                {imgSrc ? (
                  <img src={imgSrc} alt={product.description} className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-center text-stone-400 p-4">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-xs">Nessuna immagine</p>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="tech-sheet-image-input" />
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex-1" data-testid="tech-sheet-upload-btn">
                  {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                  {imgSrc ? "Cambia" : "Carica"}
                </Button>
                {imgSrc && (
                  <Button type="button" size="sm" variant="outline" onClick={handleImageDelete} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="tech-sheet-image-delete-btn">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-stone-400 italic">JPG/PNG max 5 MB. Verrà mostrata anche nel catalogo cliente e agente.</p>
            </div>

            {/* Campi scheda tecnica */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs font-semibold text-stone-500 uppercase tracking-wide px-1">
                <span>🇮🇹 Italiano</span>
                <span>🇬🇧 English (opzionale, altrimenti IT)</span>
              </div>

              <TechRow label="Peso" fieldIt="weight_it" fieldEn="weight_en" tech={tech} set={set} placeholderIt="1,2 kg" placeholderEn="1.2 kg" />
              <TechRow label="Provenienza" fieldIt="origin_it" fieldEn="origin_en" tech={tech} set={set} placeholderIt="Italia" placeholderEn="Italy" />
              <TechRow label="Confezione" fieldIt="packaging_it" fieldEn="packaging_en" tech={tech} set={set} placeholderIt="Sottovuoto" placeholderEn="Vacuum-sealed" />
              <TechRow label="Pz x confezione" fieldIt="pcs_per_pack_it" fieldEn="pcs_per_pack_en" tech={tech} set={set} placeholderIt="6 pz" placeholderEn="6 pcs" />
              <TechRow label="TMC (durata)" fieldIt="tmc_it" fieldEn="tmc_en" tech={tech} set={set} placeholderIt="24 mesi" placeholderEn="24 months" />

              {/* Altro 1 & 2 — etichetta personalizzabile */}
              <div className="border-t border-stone-200 pt-3 space-y-3">
                <p className="text-xs font-semibold text-stone-600">Campi liberi (etichetta personalizzabile per prodotto)</p>
                <CustomRow index={1} tech={tech} set={set} />
                <CustomRow index={2} tech={tech} set={set} />
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-row items-center justify-between gap-2 pt-3 border-t border-stone-100">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadPdf("it")} data-testid="tech-sheet-pdf-it-btn">
              <FileText className="w-3.5 h-3.5 mr-1" />PDF Italiano
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadPdf("en")} data-testid="tech-sheet-pdf-en-btn">
              <FileText className="w-3.5 h-3.5 mr-1" />PDF English
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="tech-sheet-cancel-btn">
              <X className="w-4 h-4 mr-1" />Chiudi
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="tech-sheet-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Salva
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TechRow({ label, fieldIt, fieldEn, tech, set, placeholderIt = "", placeholderEn = "" }) {
  return (
    <div>
      <Label className="text-xs font-medium text-stone-700 mb-1 block">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Input value={tech[fieldIt] || ""} onChange={(e) => set(fieldIt, e.target.value)} placeholder={placeholderIt} className="h-8 text-sm" data-testid={`tech-${fieldIt}`} />
        <Input value={tech[fieldEn] || ""} onChange={(e) => set(fieldEn, e.target.value)} placeholder={placeholderEn} className="h-8 text-sm bg-blue-50/30" data-testid={`tech-${fieldEn}`} />
      </div>
    </div>
  );
}


function CustomRow({ index, tech, set }) {
  const lIt = `extra_${index}_label_it`;
  const vIt = `extra_${index}_value_it`;
  const lEn = `extra_${index}_label_en`;
  const vEn = `extra_${index}_value_en`;
  return (
    <div className="space-y-1.5 bg-amber-50/30 border border-amber-200 rounded p-2">
      <Label className="text-[11px] font-semibold text-amber-800 uppercase tracking-wide">Campo libero #{index}</Label>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Input value={tech[lIt] || ""} onChange={(e) => set(lIt, e.target.value)} placeholder="Etichetta (es. Frollatura)" className="h-7 text-xs font-medium" data-testid={`tech-${lIt}`} />
          <Input value={tech[vIt] || ""} onChange={(e) => set(vIt, e.target.value)} placeholder="Valore (es. 30 giorni)" className="h-7 text-xs" data-testid={`tech-${vIt}`} />
        </div>
        <div className="space-y-1">
          <Input value={tech[lEn] || ""} onChange={(e) => set(lEn, e.target.value)} placeholder="Label (e.g. Aging)" className="h-7 text-xs font-medium bg-blue-50/30" data-testid={`tech-${lEn}`} />
          <Input value={tech[vEn] || ""} onChange={(e) => set(vEn, e.target.value)} placeholder="Value (e.g. 30 days)" className="h-7 text-xs bg-blue-50/30" data-testid={`tech-${vEn}`} />
        </div>
      </div>
    </div>
  );
}
